// A select you can search — for lists that can grow long (departments,
// programmes). Same Popover + Command pattern as SubjectCombobox, but the
// options are passed in and filtered here (no server search).
//
//   <SearchableSelect
//       value={department}            // null = the "all"/"none" choice
//       options={[ { value, label, hint? } ]}
//       nullLabel="All departments"   // optional first choice (value null)
//       placeholder="Choose…"         // shown when value is null and there is no nullLabel
//       onChange={(v) => …} />
import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export type SearchableOption = { value: string; label: string; hint?: string };

type Props = {
    value: string | null;
    options: SearchableOption[];
    onChange: (value: string | null) => void;
    nullLabel?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
    className?: string;
};

const NULL_KEY = "__null__";

export function SearchableSelect({
    value, options, onChange, nullLabel, placeholder = "Choose…",
    searchPlaceholder = "Search…", emptyText = "Nothing found.", disabled, className,
}: Props) {
    const [ open, setOpen ] = useState(false);
    const [ query, setQuery ] = useState("");

    const selected = options.find(o => o.value === value) || null;
    const shown = selected ? selected.label : (nullLabel ?? placeholder);

    // Case-insensitive match on the label and the hint; the null choice stays
    // visible while the search is empty.
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => o.label.toLowerCase().includes(q) || (o.hint || "").toLowerCase().includes(q));
    }, [ options, query ]);

    const pick = (v: string | null) => {
        onChange(v);
        setOpen(false);
        setQuery("");
    };

    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn("h-9 w-full justify-between font-normal text-sm", !selected && !nullLabel && "text-muted-foreground", className)}
                >
                    <span className="truncate">{shown}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] min-w-56 p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        <CommandGroup>
                            {nullLabel && !query.trim() && (
                                <CommandItem value={NULL_KEY} onSelect={() => pick(null)}>
                                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === null ? "opacity-100" : "opacity-0")} />
                                    {nullLabel}
                                </CommandItem>
                            )}
                            {filtered.map(o => (
                                <CommandItem key={o.value} value={o.value} onSelect={() => pick(o.value)}>
                                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                                    <div className="min-w-0">
                                        <div className="truncate">{o.label}</div>
                                        {o.hint && <div className="truncate text-xs text-muted-foreground">{o.hint}</div>}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
